/**
 * PaletteContext - Global state for active color palette
 *
 * Provides the currently selected palette and methods to change it.
 * All ColorControl components read from this to constrain color choices.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import {
  type ColorPalette,
  type PaletteSlot,
  type SlotMapping,
  defaultPalettes,
  getSlotColor
} from './paletteTypes';

interface PaletteUpdate {
  name?: string;
  colors?: string[];
  mapping?: Partial<SlotMapping>;
}

interface IPaletteContext {
  /** Currently active palette, or null if 'none' (free color mode) */
  palette: ColorPalette | null;
  /** All available palettes */
  palettes: ColorPalette[];
  /** Whether colors are constrained to a palette */
  isConstrained: boolean;
  /** Saved colors for free mode */
  savedColors: string[];
  /** Set active palette by ID, or 'none' for free color mode */
  setPaletteById: (id: string | null) => void;
  /** Get color value for a slot (returns gray if no palette active) */
  getColor: (slot: PaletteSlot) => string;
  /** Add a new palette */
  addPalette: (palette: ColorPalette) => void;
  /** Update an existing palette (name, colors array, or slot mappings) */
  updatePalette: (id: string, update: PaletteUpdate) => void;
  /** Remove a palette (can't remove last one) */
  removePalette: (id: string) => void;
  /** Save a color to free mode palette */
  saveColor: (hex: string) => void;
}

const PaletteContext = createContext<IPaletteContext | null>(null);

// Validate palette has correct structure (colors array + mapping)
function isValidPalette(p: unknown): p is ColorPalette {
  if (!p || typeof p !== 'object') return false;
  const palette = p as Record<string, unknown>;
  return (
    typeof palette.id === 'string' &&
    typeof palette.name === 'string' &&
    Array.isArray(palette.colors) &&
    typeof palette.mapping === 'object' &&
    palette.mapping !== null
  );
}

// Load palettes from localStorage or use defaults
function loadPalettes(): ColorPalette[] {
  try {
    const stored = localStorage.getItem('color-palettes');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate all palettes have correct structure
      if (Array.isArray(parsed) && parsed.every(isValidPalette)) {
        return parsed;
      }
      // Invalid format - clear and use defaults
      localStorage.removeItem('color-palettes');
    }
  } catch (e) {
    // Ignore parse errors
  }
  return defaultPalettes;
}

// Load active palette ID from localStorage ('none' for free color mode)
function loadActivePaletteId(): string | null {
  const stored = localStorage.getItem('active-palette-id');
  if (stored === 'none') return null;
  return stored || null; // Default to free mode (null)
}

// Load saved colors for free mode
function loadSavedColors(): string[] {
  try {
    const stored = localStorage.getItem('saved-colors');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.every(c => typeof c === 'string')) {
        return parsed;
      }
    }
  } catch (e) {
    // Ignore parse errors
  }
  return [];
}

export function PaletteProvider({ children }: { children: ReactNode }) {
  const [palettes, setPalettes] = useState<ColorPalette[]>(loadPalettes);
  const [activePaletteId, setActivePaletteId] = useState<string | null>(loadActivePaletteId);
  const [savedColors, setSavedColors] = useState<string[]>(loadSavedColors);

  // Null means free color mode (no constraint)
  const palette = activePaletteId ? (palettes.find(p => p.id === activePaletteId) || null) : null;
  const isConstrained = palette !== null;

  const setPaletteById = useCallback((id: string | null) => {
    setActivePaletteId(id);
    localStorage.setItem('active-palette-id', id || 'none');
  }, []);

  const getColor = useCallback((slot: PaletteSlot) => {
    if (!palette) return '#888888'; // Default gray when no palette
    return getSlotColor(palette, slot);
  }, [palette]);

  const addPalette = useCallback((newPalette: ColorPalette) => {
    setPalettes(prev => {
      const updated = [...prev, newPalette];
      localStorage.setItem('color-palettes', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const updatePalette = useCallback((id: string, update: PaletteUpdate) => {
    setPalettes(prev => {
      const updated = prev.map(p => {
        if (p.id !== id) return p;
        return {
          ...p,
          name: update.name ?? p.name,
          colors: update.colors ?? p.colors,
          mapping: update.mapping ? { ...p.mapping, ...update.mapping } : p.mapping
        };
      });
      localStorage.setItem('color-palettes', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const removePalette = useCallback((id: string) => {
    setPalettes(prev => {
      if (prev.length <= 1) return prev; // Can't remove last palette
      const updated = prev.filter(p => p.id !== id);
      localStorage.setItem('color-palettes', JSON.stringify(updated));
      // If removing active palette, switch to first available
      if (activePaletteId === id) {
        setActivePaletteId(updated[0].id);
        localStorage.setItem('active-palette-id', updated[0].id);
      }
      return updated;
    });
  }, [activePaletteId]);

  const saveColor = useCallback((hex: string) => {
    setSavedColors(prev => {
      // Don't add duplicates, and limit to 20 colors
      const normalized = hex.toLowerCase();
      if (prev.some(c => c.toLowerCase() === normalized)) return prev;
      const updated = [hex, ...prev].slice(0, 20);
      localStorage.setItem('saved-colors', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <PaletteContext.Provider value={{
      palette,
      palettes,
      isConstrained,
      savedColors,
      setPaletteById,
      getColor,
      addPalette,
      updatePalette,
      removePalette,
      saveColor
    }}>
      {children}
    </PaletteContext.Provider>
  );
}

export function usePalette(): IPaletteContext {
  const context = useContext(PaletteContext);
  if (!context) {
    throw new Error('usePalette must be used within PaletteProvider');
  }
  return context;
}
